import React from 'react'
import moment from 'moment'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { translate } from 'react-i18next'
import * as translationService from '../../services/translationService'
import { getCurrentStepType } from '../../services/stepService'

import { Element, scroller } from 'react-scroll'
import { validateRentIntention } from '../../utils/validation'
import {
  TextContainer,
  DisplayText,
  DatePicker,
  OptionList,
  TextField,
  MobileNumber,
  PlaceAutocomplete,
  AddressField,
  Stack,
  Notice,
  CurrencyField,
} from './../../components'
import { updateApplicationInformation, softUpdateApplicationInformation } from '../../actions/FlowAction'
import toPath from 'lodash/toPath'
import SceneComponent from './SceneComponent'
import { clone, isEmpty } from 'ramda'

class RentIntentionScene extends SceneComponent {
  static propTypes = {
    updateApplicationInformation: PropTypes.any,
    step: PropTypes.any,
  }

  state = {
    fieldErrors: [],
    reasonForRent: '',
    reasonForRentOther: '',
    currentResidenceRentPrice: '',
    currentResidenceAddress: {
      number: '',
      street: '',
      city: '',
      zip: '',
      countryId: '',
    },
    currentResidenceOwner: {},
    currentResidenceType: '',
    dateOfOccupancy: null,
    dateToggle: null,
  }

  componentDidMount(...args) {
    super.componentDidMount.apply(this, args)
    const { application } = clone(this.props)
    const newState = {}
    Object.keys(this.state).map(item => (newState[item] = application[item] || this.state[item]))

    const todayDate = moment()
      .hours(12)
      .minutes(0)
      .add(1, 'd')
    const newSelectedDate = moment(newState.dateOfOccupancy)
    if (newState.dateOfOccupancy && newSelectedDate.isBefore(todayDate)) {
      newState.dateToggle = 'today'
      newState.dateOfOccupancy = todayDate
    } else if (newState.dateOfOccupancy && newSelectedDate.isAfter(todayDate)) {
      newState.dateToggle = 'specificDate'
      newState.dateOfOccupancy = newSelectedDate
    }

    this.setState(prevState => {
      return { ...prevState, ...newState }
    })
  }

  onSubmit = (shouldValidate = true) => {
    const {
      reasonForRent,
      reasonForRentOther,
      currentResidenceRentPrice,
      currentResidenceAddress,
      currentResidenceOwner,
      dateOfOccupancy,
      currentResidenceType,
    } = this.state

    if (shouldValidate) {
      return validateRentIntention({
        reasonForRent,
        reasonForRentOther,
        currentResidenceRentPrice,
        currentResidenceAddress,
        currentResidenceOwner,
        dateOfOccupancy,
        currentResidenceType,
      })
        .then(() =>
          this.props.updateApplicationInformation({
            reasonForRent,
            reasonForRentOther,
            currentResidenceRentPrice,
            currentResidenceAddress,
            currentResidenceOwner,
            dateOfOccupancy,
            currentResidenceType,
          }),
        )
        .catch(fieldErrors => {
          console.warn('RentIntentionScene, onSubmit', { fieldErrors })
          this.setState(
            {
              fieldErrors,
            },
            () => {
              scroller.scrollTo('mainError', {
                smooth: true,
                offset: -40, // Scrolls to element - 40 pixels down the page
                containerId: this.props.inModal && 'sceneModal',
              })
            },
          )
          throw fieldErrors
        })
    } else {
      return this.props.updateApplicationInformation({
        reasonForRent,
        reasonForRentOther,
        currentResidenceRentPrice,
        currentResidenceAddress,
        currentResidenceOwner,
        dateOfOccupancy,
        currentResidenceType,
      })
    }
  }

  onDateChange = (date, name) => {
    if (!date) return
    this.setState({
      [name]: moment(date)
        .hours(12)
        .minutes(0),
    })
  }

  onChange = ({ target: { name, value } }) => {
    const path = toPath(name)
    if (path.length > 1) {
      this.setState(prevState => ({
        [path[0]]: {
          ...prevState[path[0]],
          [path[1]]: value,
        },
      }))
    } else {
      this.setState({
        [name]: value,
      })
    }
  }

  onOptionListChange = (value, name) => {
    const todayDate = moment()
      .add(1, 'd')
      .hours(12)
      .minutes(0)

    if (name === 'dateToggle' && value === 'today') {
      this.onChange({
        target: {
          name: 'dateOfOccupancy',
          value: todayDate,
        },
      })
    } else if (name === 'dateToggle' && value === 'specificDate') {
      if (!this.state.date) {
        this.setState({ dateOfOccupancy: todayDate })
      }
    }

    this.onChange({ target: { name, value: value } })
  }

  handleGoogleAddressSelect = address => {
    this.setState({
      currentResidenceAddress: address,
    })
  }

  handleAddressChange = ({ target: { value, name } }) => {
    const { currentResidenceAddress } = this.state
    currentResidenceAddress[toPath(name)[1]] = value
    this.setState({ currentResidenceAddress })
  }

  onCheckboxChange = ({ target: { checked, name } }) => {
    this.onChange({ target: { name, value: checked } })
  }

  render() {
    const { step, inModal } = this.props

    const {
      error,
      fieldErrors = [],
      reasonForRent,
      reasonForRentOther,
      currentResidenceRentPrice,
      currentResidenceAddress,
      currentResidenceOwner,
      dateToggle,
      dateOfOccupancy,
      currentResidenceType,
    } = this.state
    const stepType = getCurrentStepType(step)
    const isFirstPhase = stepType === 'VISIT_REQUEST_STEPS'

    return (
      <TextContainer>
        {!inModal && (
          <DisplayText>
            {translationService.rentIntentionTitle(step || 1)}{' '}
            {/* {step === 2 && <small>({translationService.t('system.optional')})</small>} */}
          </DisplayText>
        )}
        {!isEmpty(fieldErrors) && (
          <Element name="mainError">
            <Notice type="error">{translationService.intakeFormErrors()}</Notice>
          </Element>
        )}
        <OptionList
          title={translationService.rentIntentionRentStartDateLabel()}
          name="dateToggle"
          options={[
            { label: translationService.rentIntentionAsapLabel(), value: 'today' },
            {
              label: translationService.rentIntentionExactDateLabel(),
              value: 'specificDate',
              renderChildren: isSelected => {
                // const extraProps = {}
                // if (window.matchMedia("(max-width: 350px)").matches) {
                //   extraProps.withFullScreenPortal = true
                // }
                return (
                  isSelected && (
                    <div>
                      <DatePicker
                        // id="startDate"
                        date={dateOfOccupancy}
                        isOutsideRange={date => date.isBefore(new moment())}
                        error={error}
                        onDateChange={date => this.onDateChange(date, 'dateOfOccupancy')}
                      />
                    </div>
                  )
                )
              },
            },
          ]}
          selected={dateToggle}
          error={fieldErrors.dateOfOccupancy}
          onChange={this.onOptionListChange}
        />
        {!isFirstPhase && (
          <React.Fragment>
            <OptionList
              title={translationService.t('intake.rent.intention.reason')}
              name="reasonForRent"
              optional={true}
              options={[
                {
                  label: translationService.t('INTAKE_RENT_INTENTION_FIRST_HOME'),
                  value: 'INTAKE_RENT_INTENTION_FIRST_HOME',
                },
                { label: translationService.t('INTAKE_RENT_INTENTION_BUDGET'), value: 'INTAKE_RENT_INTENTION_BUDGET' },
                {
                  label: translationService.t('INTAKE_RENT_INTENTION_FAMILY_EXPANSION'),
                  value: 'INTAKE_RENT_INTENTION_FAMILY_EXPANSION',
                },
                {
                  label: translationService.t('INTAKE_RENT_INTENTION_BIGGER_HOME'),
                  value: 'INTAKE_RENT_INTENTION_BIGGER_HOME',
                },
                {
                  label: translationService.t('INTAKE_RENT_INTENTION_OTHER'),
                  value: 'INTAKE_RENT_INTENTION_OTHER',
                  renderChildren: isSelected => {
                    return (
                      isSelected && (
                        <TextField
                          label={translationService.t('intake.rent.intention.other.reason')}
                          multiline={true}
                          name="reasonForRentOther"
                          id="reasonForRentOther"
                          onChange={this.onChange}
                          value={reasonForRentOther}
                        />
                      )
                    )
                  },
                },
              ]}
              selected={reasonForRent}
              error={fieldErrors && fieldErrors.reasonForRent}
              onChange={this.onOptionListChange}
            />
            <DisplayText size="small">
              {translationService.t('intake.contract.current.address')}{' '}
              <small>{translationService.t('system.optional')}</small>
            </DisplayText>
            <PlaceAutocomplete onSelect={this.handleGoogleAddressSelect} />
            <AddressField
              required
              type="text"
              name="currentResidenceAddress"
              id="currentResidenceAddress"
              address={{ ...currentResidenceAddress }}
              onChange={this.handleAddressChange}
            />
            <OptionList
              title={translationService.t('intake.contract.current.residence.type')}
              name="currentResidenceType"
              optional={true}
              options={[
                {
                  label: translationService.t('INTAKE_CONTRACT_RESIDENCE_TYPE_OWNER'),
                  value: 'INTAKE_CONTRACT_RESIDENCE_TYPE_OWNER',
                },
                {
                  label: translationService.t('INTAKE_CONTRACT_RESIDENCE_TYPE_TENANT'),
                  value: 'INTAKE_CONTRACT_RESIDENCE_TYPE_TENANT',
                  renderChildren: isSelected => {
                    return (
                      isSelected && (
                        <TextContainer>
                          <DisplayText size="small">
                            {translationService.t('intake.contract.current.residence.extra.info')}{' '}
                            <small>{translationService.t('system.optional')}</small>
                          </DisplayText>
                          <CurrencyField
                            label={translationService.t('intake.rent.intention.current.rent.price')}
                            name="currentResidenceRentPrice"
                            id="currentResidenceRentPrice"
                            onChange={this.onChange}
                            suffix="EUR"
                            value={currentResidenceRentPrice}
                          />
                          <DisplayText size="small">
                            {translationService.t('intake.contract.current.landlord')}{' '}
                            <small>{translationService.t('system.optional')}</small>
                          </DisplayText>
                          <div>
                            <Stack distribution="fill">
                              <TextField
                                error={fieldErrors.currentResidenceOwner && fieldErrors.currentResidenceOwner.firstname}
                                label={translationService.t('system.firstname')}
                                id="firstname"
                                name="currentResidenceOwner.firstname"
                                value={currentResidenceOwner.firstname}
                                onChange={event => this.onChange(event)}
                              />
                              <TextField
                                error={fieldErrors.currentResidenceOwner && fieldErrors.currentResidenceOwner.lastname}
                                label={translationService.t('system.lastname')}
                                id="lastname"
                                name="currentResidenceOwner.lastname"
                                value={currentResidenceOwner.lastname}
                                onChange={event => this.onChange(event)}
                              />
                            </Stack>
                          </div>
                          <TextField
                            error={fieldErrors.currentResidenceOwner && fieldErrors.currentResidenceOwner.email}
                            label={translationService.t('system.email')}
                            id="email"
                            name="currentResidenceOwner.email"
                            value={currentResidenceOwner.email}
                            onChange={event => this.onChange(event)}
                          />
                          <MobileNumber
                            error={fieldErrors.currentResidenceOwner && fieldErrors.currentResidenceOwner.phone}
                            label={translationService.t('system.mobile_number')}
                            id="phone"
                            name="currentResidenceOwner.phone"
                            value={currentResidenceOwner.phone}
                            onChange={event => this.onChange(event)}
                          />
                        </TextContainer>
                      )
                    )
                  },
                },
              ]}
              selected={currentResidenceType}
              onChange={this.onOptionListChange}
            />
          </React.Fragment>
        )}
        {/*<pre>{JSON.stringify(this.state, null, 4)}</pre>*/}
      </TextContainer>
    )
  }
}

const mapStateToProps = ({ FlowReducer }) => {
  const { application, step } = FlowReducer
  return {
    application,
    step,
  }
}
const mapDispatchToProps = dispatch => ({
  updateApplicationInformation: information => dispatch(updateApplicationInformation(information)),
  softUpdateApplicationInformation: information => dispatch(softUpdateApplicationInformation(information)),
})

export default compose(
  translate('translations'),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(RentIntentionScene)



// WEBPACK FOOTER //
// ./src/views/scenes/RentIntentionScene.js